import { FC, useState, Dispatch, SetStateAction, DragEvent } from 'react';
import cx from 'classnames/bind';
import { useMediaQuery } from 'react-responsive';

import { Layout } from '@/components/Layout';
import { Message } from '@/components/Message';
import { Answers } from '@/components/Answers';
import { Button } from '@/components/Button';
import { Animation } from '@/components/Animation';
import SelectedAnswer from '@/components/SelectedAnswer/SelectedAnswer';
import ManagerAvatar from '@/assets/managerAvatar.png';
import Buyer1AvatarIcon from '@/assets/buyer1Avatar.png';
import Buyer2AvatarIcon from '@/assets/buyer2Avatar.png';
import Buyer3AvatarIcon from '@/assets/buyer3Avatar.png';
import Buyer4AvatarIcon from '@/assets/buyer4Avatar.png';
import AnimationTraining from '@/assets/animations/training.json';
import AnimationBuyer1 from '@/assets/animations/buyer1.json';
import AnimationBuyer2 from '@/assets/animations/buyer2.json';
import AnimationBuyer3 from '@/assets/animations/buyer3.json';
import AnimationBuyer4 from '@/assets/animations/buyer4.gif';
import CashboxDesktop from '@/assets/cashboxDesktop.png';
import { Header } from '../../Header';
import type { IQuestion } from '@/types/question';
import type { IGame } from '@/types/game';

import styles from './Step3.module.scss';

interface Step3Props {
  setStep: (value: number) => void;
  question: IQuestion;
  selectedAnswer: string[];
  setSelectedAnswer: (value: string[]) => void;
  numberQuestion: number;
  questions: IQuestion[];
  idGame: number;
  scenarios: IGame[];
  points: number;
  isRightAnswer: boolean;
  setPoints: Dispatch<SetStateAction<number>>;
  setResponseStatus: Dispatch<SetStateAction<{ [x: number]: boolean | null }>>;
  responseStatus: { [x: number]: boolean | null };
  costPerQuestion: number;
  userToken: string;
}

const cn = cx.bind(styles);

const Step3: FC<Step3Props> = ({
  setStep,
  question,
  selectedAnswer,
  setSelectedAnswer,
  numberQuestion,
  questions,
  idGame,
  scenarios,
  points,
  isRightAnswer,
  setPoints,
  setResponseStatus,
  responseStatus,
  costPerQuestion,
  userToken
}) => {
  const [answers, setAnswers] = useState(question.answers);
  const [state, setState] = useState(0);

  const isDesctop = useMediaQuery({ query: '(min-width: 1440px)' });

  const currentScenarioIndex = scenarios.findIndex(({ id }) => id === idGame);

  const srcBuyerAvatar = !scenarios[currentScenarioIndex]?.isScore
    ? ManagerAvatar
    : currentScenarioIndex === 0 ||
      (currentScenarioIndex + (!scenarios[0].isScore ? 3 : 0)) % 4 === 0
    ? Buyer1AvatarIcon
    : (currentScenarioIndex + (!scenarios[0].isScore ? 2 : 3)) % 4 === 0
    ? Buyer2AvatarIcon
    : (currentScenarioIndex + (!scenarios[0].isScore ? 1 : 2)) % 4 === 0
    ? Buyer3AvatarIcon
    : Buyer4AvatarIcon;

  const isCompletedScenario = scenarios.find(({ id }) => id === idGame)?.status;

  const isScenarioWithPoints = scenarios.find(({ id }) => id === idGame)
    ?.isScore;

  const startTraining = () => {
    if (!isCompletedScenario && isRightAnswer && isScenarioWithPoints) {
      setPoints((prev: number) => prev + costPerQuestion);
    }

    if (isRightAnswer) {
      setResponseStatus((prev) => ({ ...prev, [numberQuestion - 1]: true }));

      fetch(
        'https://admin.informer.jti-games.dev.familyagency.ru/api/custom-logs/store',
        {
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          method: 'POST',
          body: JSON.stringify({
            'token': userToken,
            'game_id': idGame,
            'question_id' : numberQuestion,
            'action' : 'правильный ответ',
            'result' : isRightAnswer.toString(),
            'available_score' : points,
            'awarded_score' : costPerQuestion,
          }),
        },
      )
    } else {
      setResponseStatus((prev) => ({ ...prev, [numberQuestion - 1]: false }));

      fetch(
        'https://admin.informer.jti-games.dev.familyagency.ru/api/custom-logs/store',
        {
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          method: 'POST',
          body: JSON.stringify({
            'token': userToken,
            'game_id': idGame,
            'question_id' : numberQuestion,
            'action' : 'неправильный ответ',
            'result' : isRightAnswer.toString(),
            'available_score' : points,
            'awarded_score' : costPerQuestion,
          }),
        },
      )
    }

    setStep(12);
  };

  const dragStartHandler = (e: DragEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    e.dataTransfer.setData('answer', target.innerText);
  };

  const deleteMessage = (message: string) => {
    setAnswers([
      ...answers,
      ...question.answers.filter((item) => item.text === message),
    ]);
    setSelectedAnswer(selectedAnswer.filter((item) => item !== message));
    setState((prevStep: number) => prevStep + 1);
  };

  const dropHandler = (e: DragEvent<HTMLDivElement>) => {
    const value = e.dataTransfer.getData('answer').trim();
    const answersFilter = answers.filter((item) => item.text !== value);
    setAnswers(answersFilter);
    setSelectedAnswer([...selectedAnswer, value]);
    setState((prevStep: number) => prevStep + 1);
  };

  return (
    <>
      <div className={cn('background')} />
      <div className={cn('imgWrapper', 'imgWrapper-desctop')}>
        {!scenarios[currentScenarioIndex]?.isScore && (
          <Animation animation={AnimationTraining} />
        )}
        {scenarios[currentScenarioIndex]?.isScore &&
          (currentScenarioIndex === 0 ||
            (currentScenarioIndex + (!scenarios[0].isScore ? 3 : 0)) % 4 ===
              0) && <Animation animation={AnimationBuyer1} />}
        {scenarios[currentScenarioIndex]?.isScore &&
          (currentScenarioIndex + (!scenarios[0].isScore ? 2 : 3)) % 4 ===
            0 && <Animation animation={AnimationBuyer2} />}
        {scenarios[currentScenarioIndex]?.isScore &&
          (currentScenarioIndex + (!scenarios[0].isScore ? 1 : 2)) % 4 ===
            0 && <Animation animation={AnimationBuyer3} />}
        {scenarios[currentScenarioIndex]?.isScore &&
          (currentScenarioIndex + (!scenarios[0].isScore ? 0 : 1)) % 4 ===
            0 && (
            <img src={AnimationBuyer4} className={cn('img')} alt="Покупатель" />
          )}
      </div>
      <img src={CashboxDesktop} className={cn('imgCashbox')} alt="Касса" />
      <Layout className={cn('container')}>
        <Header
          numberQuestion={numberQuestion}
          questions={questions}
          className={cn('header')}
          scenarios={scenarios}
          points={points}
          responseStatus={responseStatus}
        />
        <div className={cn('wrapper')}>
          {!isDesctop && (
            <div className={cn('imgWrapper')}>
              <img
                src={srcBuyerAvatar}
                className={cn('img')}
                alt="Консультант"
              />
            </div>
          )}
          <div className={cn('center')}>
            <Message className={cn('message')} user="buyer">
              {question.text}
            </Message>
            <SelectedAnswer
              className={cn('answer')}
              state={state}
              selectedAnswer={selectedAnswer}
              deleteMessage={deleteMessage}
              onDrop={(e) => dropHandler(e)}
              isMultiple={question.multiply}
            />
          </div>
          <Answers
            className={cn('answers')}
            answers={answers}
            selectedAnswer={selectedAnswer}
            onDragStart={dragStartHandler}
            state={state}
            isMultiple={question.multiply}
          />
        </div>
        {selectedAnswer.length > 0 && (
          <Button handleClick={startTraining} className={cn('button')}>
            Ответить
          </Button>
        )}
      </Layout>
    </>
  );
};

export default Step3;
